import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { toast } from "react-toastify";
import TextBox from "../../components/Common/TextBox";
import SelectDropDown from "../../components/Common/SelectDropDown";
import CheckBox from "../../components/Common/CheckBox";
import DeleteModal from "../../components/Common/DeleteModal";
import GenericTable from "../../components/GenericTable";
import ActionDropdown from "../../components/AgentDashboard/ActionDropdown";
import { useCurrentWebsite } from "../../hooks/useCurrentWebsite";
import { pageURLs } from "../../constants/pageURLs";
import * as adminWidgetService from "../../services/v2/adminWidgetService";

const ADMIN_WIDGETS_KEY = "adminWidgets";

// Tenants offered for widgets. cove/stellar have no defaultMarket in their
// config, so their market must be entered manually (handled on tenant select).
const MLS_TENANT_OPTIONS = [
  { value: "lar", label: "LAR — Lubbock, TX" },
  { value: "blc", label: "BLC / MIBOR — Indianapolis, IN" },
  { value: "cove", label: "Cove" },
  { value: "stellar", label: "Stellar" },
  { value: "momentum", label: "Momentum — Kingman, AZ" },
];

const emptyForm = {
  id: null,
  name: "",
  tenant: null,
  marketCity: "",
  marketState: "",
  marketLat: "",
  marketLng: "",
  showSortBy: true,
  domains: [],
};

const CreateIframeWidget = () => {
  const { isGitchaDefaultWebsite } = useCurrentWebsite();
  const queryClient = useQueryClient();

  const [form, setForm] = useState(emptyForm);
  const [domainInput, setDomainInput] = useState("");
  const [deleteId, setDeleteId] = useState(null);

  const widgetsQuery = useQuery({
    queryKey: [ADMIN_WIDGETS_KEY],
    queryFn: async () => {
      const res = await adminWidgetService.listWidgets();
      if (res?.errorCode !== 0) {
        throw new Error(res?.message || "Failed to load widgets");
      }
      return res?.result || [];
    },
    enabled: isGitchaDefaultWebsite,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (widgetsQuery.isError) toast.error("Could not load widgets.");
  }, [widgetsQuery.isError]);

  const invalidateWidgets = () =>
    queryClient.invalidateQueries({ queryKey: [ADMIN_WIDGETS_KEY] });

  const createMutation = useMutation({
    mutationFn: (payload) => adminWidgetService.createWidget(payload),
    onSuccess: (res) => {
      if (res?.errorCode !== 0) {
        return toast.error(res?.message || "Could not save widget.");
      }
      toast.success("Widget created.");
      resetForm();
      invalidateWidgets();
    },
    onError: () => toast.error("Could not save widget."),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) =>
      adminWidgetService.updateWidget(id, payload),
    onSuccess: (res) => {
      if (res?.errorCode !== 0) {
        return toast.error(res?.message || "Could not save widget.");
      }
      toast.success("Widget updated.");
      resetForm();
      invalidateWidgets();
    },
    onError: () => toast.error("Could not save widget."),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminWidgetService.deleteWidget(id),
    onSuccess: (res, id) => {
      if (res?.errorCode !== 0) {
        return toast.error(res?.message || "Could not delete widget.");
      }
      toast.success("Widget deleted.");
      if (form.id === id) resetForm();
      invalidateWidgets();
    },
    onError: () => toast.error("Could not delete widget."),
    onSettled: () => setDeleteId(null),
  });

  // Admin tool is Gitcha-only; never expose it on a BLS subdomain.
  if (!isGitchaDefaultWebsite) {
    return <Navigate to={pageURLs.home} replace />;
  }

  const widgets = widgetsQuery.data ?? [];
  const saving = createMutation.isPending || updateMutation.isPending;

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleTenantChange = async (option) => {
    setField("tenant", option);
    try {
      const res = await fetch(`/configs/tenants/${option.value}.json`);
      const cfg = res.ok ? await res.json() : null;
      const market = cfg?.defaultMarket;
      setForm((f) => ({
        ...f,
        tenant: option,
        marketCity: market?.city || "",
        marketState: market?.state || "",
        marketLat: market?.latitude ?? "",
        marketLng: market?.longitude ?? "",
      }));
    } catch (e) {
      // No config/market — admin enters market manually.
    }
  };

  const addDomain = () => {
    const value = domainInput.trim().toLowerCase();
    if (!value) return;
    if (!form.domains.includes(value)) setField("domains", [...form.domains, value]);
    setDomainInput("");
  };

  const removeDomain = (value) =>
    setField("domains", form.domains.filter((d) => d !== value));

  function resetForm() {
    setForm(emptyForm);
    setDomainInput("");
  }

  const handleSubmit = () => {
    if (!form.name.trim()) return toast.error("Widget name is required.");
    if (!form.tenant) return toast.error("Select an MLS tenant.");
    if (form.domains.length === 0)
      return toast.error("Add at least one authorized domain.");

    const payload = {
      name: form.name.trim(),
      tenant_slug: form.tenant.value,
      market_city: form.marketCity || null,
      market_state: form.marketState || null,
      market_lat: form.marketLat === "" ? null : form.marketLat,
      market_lng: form.marketLng === "" ? null : form.marketLng,
      filters_config: {
        sort: {
          enabled: form.showSortBy,
          options: ["newest", "oldest"],
          default: "newest",
        },
      },
      allowed_domains: form.domains,
    };

    if (form.id) {
      updateMutation.mutate({ id: form.id, payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const handleEdit = (id) => {
    const w = widgets.find((x) => x.id === id);
    if (!w) return;
    setForm({
      id: w.id,
      name: w.name || "",
      tenant: MLS_TENANT_OPTIONS.find((o) => o.value === w.tenant_slug) || null,
      marketCity: w.market_city || "",
      marketState: w.market_state || "",
      marketLat: w.market_lat ?? "",
      marketLng: w.market_lng ?? "",
      showSortBy: w.filters_config?.sort?.enabled ?? true,
      domains: w.allowed_domains || [],
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const renderHeader = () => (
    <thead>
      <tr>
        <th>Name</th>
        <th>MLS tenant</th>
        <th>Market</th>
        <th>Filters</th>
        <th>Authorized domains</th>
        <th>Status</th>
        <th>Embed</th>
        <th></th>
      </tr>
    </thead>
  );

  const renderRow = (w) => (
    <>
      <td>{w.name}</td>
      <td>{w.tenant_slug}</td>
      <td>
        {[w.market_city, w.market_state].filter(Boolean).join(", ") || "—"}
      </td>
      <td>{w.filters_config?.sort?.enabled ? "Sort By" : "—"}</td>
      <td className="domains-cell">{(w.allowed_domains || []).join(", ") || "—"}</td>
      <td>{w.is_active ? "Active" : "Inactive"}</td>
      <td>
        <CopyToClipboard
          text={w.embedSnippet || ""}
          onCopy={() => toast.success("Embed snippet copied.")}
        >
          <button type="button" className="btn btn-tertiary btn-sm mr-2">
            Copy
          </button>
        </CopyToClipboard>
        <button
          type="button"
          className="btn btn-tertiary btn-sm"
          onClick={() => window.open(`/embed/w/${w.widget_key}`, "_blank")}
        >
          Preview
        </button>
      </td>
      <td>
        <ActionDropdown
          itemId={w.id}
          editText="Edit"
          deleteText="Delete"
          onItemEdit={handleEdit}
          onItemDelete={(id) => setDeleteId(id)}
        />
      </td>
    </>
  );

  return (
    <div className="agent-no-login admin-iframe-widgets">
      <div className="container py-4">
        <h1 className="f-size-24 mb-4">Create Iframe Widget</h1>

        <div className="widget-form card p-4 mb-5">
          <div className="row">
            <div className="col-md-6 mb-3">
              <TextBox
                label="Widget name"
                placeholder="e.g. MIBOR homepage widget"
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
              />
            </div>
            <div className="col-md-6 mb-3">
              <label className="form-label">MLS tenant</label>
              <SelectDropDown
                options={MLS_TENANT_OPTIONS}
                value={form.tenant}
                onChange={handleTenantChange}
                placeholder="Select an MLS"
                classNamePrefix="select"
                errorClass="d-none"
              />
            </div>
            <div className="col-md-6 mb-3">
              <TextBox
                label="Market city"
                value={form.marketCity}
                onChange={(e) => setField("marketCity", e.target.value)}
              />
            </div>
            <div className="col-md-6 mb-3">
              <TextBox
                label="Market state"
                value={form.marketState}
                onChange={(e) => setField("marketState", e.target.value)}
              />
            </div>

            <div className="col-12 mb-3 d-flex align-items-center">
              <CheckBox
                id="showSortBy"
                checked={form.showSortBy}
                onChange={(e) => setField("showSortBy", e.target.checked)}
              />
              <label htmlFor="showSortBy" className="ms-2 mb-0">
                Show "Sort By" control (Most recent / Oldest)
              </label>
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Authorized domains</label>
              <div className="d-flex domain-input-row">
                <TextBox
                  placeholder="e.g. mibor.com"
                  value={domainInput}
                  onChange={(e) => setDomainInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      addDomain();
                    }
                  }}
                  notShowError={true}
                />
                <button
                  type="button"
                  className="btn btn-tertiary ms-2"
                  onClick={addDomain}
                >
                  Add
                </button>
              </div>
              <div className="domain-chips mt-2">
                {form.domains.map((d) => (
                  <span key={d} className="domain-chip">
                    {d}
                    <button
                      type="button"
                      className="chip-remove"
                      onClick={() => removeDomain(d)}
                      aria-label={`Remove ${d}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="d-flex gap-2 mt-2">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={saving}
            >
              {form.id ? "Save changes" : "Create Iframe Widget"}
            </button>
            {form.id && (
              <button
                type="button"
                className="btn btn-tertiary"
                onClick={resetForm}
              >
                Cancel edit
              </button>
            )}
          </div>
        </div>

        <h2 className="f-size-20 mb-3">Widgets</h2>
        {widgetsQuery.isLoading ? (
          <p className="text-muted">Loading widgets…</p>
        ) : widgets.length === 0 ? (
          <p className="text-muted">No widgets yet — create one above.</p>
        ) : (
          <GenericTable
            data={widgets}
            renderHeader={renderHeader}
            renderRow={renderRow}
            className="widget-table"
          />
        )}
      </div>

      <DeleteModal
        show={!!deleteId}
        title="Delete widget"
        message="Are you sure you want to delete this widget? Embeds using it will stop working."
        buttonText="Delete"
        handleConfirm={() => deleteMutation.mutate(deleteId)}
        onHide={() => setDeleteId(null)}
      />
    </div>
  );
};

export default CreateIframeWidget;
